import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  BookOpen, 
  PenTool, 
  FileText, 
  Plus, 
  Clock, 
  ChevronLeft,
  Play,
  Share2,
  Trash2,
  Star
} from "lucide-react";
import { listTexts, getSampleTexts, deleteText } from "../lib/api";

export default function LectureDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [texts, setTexts] = useState([]);
  const [sampleTexts, setSampleTexts] = useState([]);
  const [loading, setLoading] = useState(true);

  const isTeacher = user?.role === "teacher";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [textsRes, samplesRes] = await Promise.all([
        listTexts(),
        getSampleTexts()
      ]);
      setTexts(textsRes.data);
      setSampleTexts(samplesRes.data);
    } catch (err) {
      console.error("Failed to load data:", err);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce texte ?")) return;
    try {
      await deleteText(id);
      setTexts(texts.filter(t => t.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  return (
    <div 
      data-testid="lecture-dashboard"
      className="min-h-screen bg-gradient-to-br from-blue-50 to-white"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-blue-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-home-btn"
              onClick={() => navigate("/home")}
              className="p-2 hover:bg-blue-100 rounded-full transition-colors"
            >
              <ChevronLeft className="text-blue-600" size={24} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shadow-[0_4px_0_rgb(29,78,216)]">
                <BookOpen className="text-white" size={24} />
              </div>
              <div>
                <h1 className="font-fredoka text-2xl font-bold text-blue-700">
                  Lecture
                </h1>
                <p className="text-sm text-slate-500 font-nunito">
                  Apprendre a lire
                </p>
              </div>
            </div>
          </div>

          {isTeacher && (
            <button
              data-testid="new-text-btn"
              onClick={() => navigate("/editor")}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-5 py-3 rounded-full font-fredoka font-bold shadow-[0_4px_0_rgb(29,78,216)] active:shadow-none active:translate-y-1 transition-all"
            >
              <Plus size={20} />
              <span>Nouveau texte</span>
            </button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full mx-auto"></div>
            <p className="mt-4 text-slate-500 font-nunito">Chargement...</p>
          </div>
        ) : (
          <>
            {/* My Texts */}
            {texts.length > 0 && (
              <section className="mb-10">
                <h2 className="font-fredoka text-xl font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <FileText className="text-blue-500" size={24} />
                  {isTeacher ? "Mes textes" : "Textes a lire"}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {texts.map((text) => (
                    <div
                      key={text.id}
                      className="bg-white border-2 border-slate-100 rounded-2xl p-5 hover:border-blue-200 hover:shadow-lg transition-all"
                    >
                      <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-2">
                        {text.title}
                      </h3>
                      <p className="text-slate-500 text-sm line-clamp-2 mb-4">
                        {text.raw_text?.substring(0, 100)}...
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <button
                          data-testid={`read-text-${text.id}`}
                          onClick={() => navigate(`/reader/${text.id}`)}
                          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-colors"
                        >
                          <Play size={16} />
                          Lire
                        </button>
                        
                        {isTeacher && (
                          <div className="flex items-center gap-2">
                            <button
                              data-testid={`edit-text-${text.id}`}
                              onClick={() => navigate(`/editor/${text.id}`)}
                              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                            >
                              <PenTool size={18} className="text-slate-500" />
                            </button>
                            <button
                              data-testid={`delete-text-${text.id}`}
                              onClick={() => handleDelete(text.id)}
                              className="p-2 hover:bg-red-50 rounded-full transition-colors"
                            >
                              <Trash2 size={18} className="text-red-400" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Sample Texts */}
            <section className="mb-10">
              <h2 className="font-fredoka text-xl font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Star className="text-yellow-500" size={24} />
                Textes exemples
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sampleTexts.map((sample, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-100 rounded-2xl p-5 hover:border-yellow-300 hover:shadow-lg transition-all"
                  >
                    <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-2">
                      {sample.title}
                    </h3>
                    <p className="text-slate-500 text-sm line-clamp-2 mb-4">
                      {sample.text?.substring(0, 100)}...
                    </p>
                    
                    <button
                      data-testid={`sample-text-${index}`}
                      onClick={() => {
                        // Create a temporary text for reading
                        navigate(`/editor?sample=${index}`);
                      }}
                      className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-colors"
                    >
                      <Play size={16} />
                      Essayer
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* Empty State */}
            {texts.length === 0 && (
              <div className="text-center py-12 bg-white rounded-3xl border-2 border-dashed border-slate-200">
                <img 
                  src="/LEcoleDeNavi5.png" 
                  alt="Navi" 
                  className="h-24 mx-auto mb-4 opacity-50"
                />
                <h3 className="font-fredoka text-xl text-slate-600 mb-2">
                  Pas encore de texte
                </h3>
                <p className="text-slate-400 mb-4">
                  {isTeacher 
                    ? "Creez votre premier texte pour vos eleves !" 
                    : "Demandez a votre enseignant de preparer un texte"
                  }
                </p>
                {isTeacher && (
                  <button
                    onClick={() => navigate("/editor")}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full font-fredoka font-bold transition-colors"
                  >
                    Creer un texte
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
